<template>
  <Layout>
    <section class="marketplace--order-history scroll-x-mobile">
      <header class="hide sm:flex sm:items-start sm:justify-between sm:flex-wrap sm:gap-md">
        <h2 class="marketplace-heading mb-md sm:mb-0">{{ 'Order History' | t }}</h2>
        <SloField class="m-0">
          <SloInput
            v-if="!loading && pagination.totalResults || queryParams.searchTerm"
            v-model="queryParams.searchTerm"
            type="text"
            :placeholder="'Search orders' | t"
          >
            <template v-slot:start>
              <SloIcon name="MagnifyingGlass" />
            </template>
          </SloInput>
        </SloField>
      </header>
      <main>
        <section class="marketplace--order-history block sm:hide">
          <header class="mb-sm">
            <h2 class="marketplace-heading mb-md md:mb-0">{{ 'Order History' | t }}</h2>
            <SloField class="m-0">
              <SloInput
                v-if="!loading && pagination.totalResults || queryParams.searchTerm"
                v-model="queryParams.searchTerm"
                :placeholder="'Search orders' | t"
                type="text"
              >
                <template v-slot:start>
                  <SloIcon name="MagnifyingGlass" />
                </template>
              </SloInput>
            </SloField>
          </header>
        </section>
        <EmptyCartTeaser v-if="!loading && !isSearchingOrFiltering && !pagination.totalResults">
          <template v-slot:header>
            <h2 class="text-xl fw-strong">{{ 'You have no orders' | t }}</h2>
            <p class="m-0">{{ 'Shop the following categories for Sloan parts' | t }}</p>
          </template>
        </EmptyCartTeaser>
        <div v-else>
          <SloLoader :loading="loading" size="xl">
            <SloTabs
              items="start"
              default-value="all"
              :value="queryParams.status"
              :is-filter-tabs="true"
              @input="handleStatusChange"
            >
              <SloTab name="all" :title="'All' | t" />
              <SloTab name="open" :title="'Open' | t" />
              <SloTab name="shipped" :title="'Shipped' | t" />
              <SloTab name="cancelled" :title="'Cancelled' | t" />
            </SloTabs>
            <div class="flex items-center justify-start gap-md mt-md">
              <p v-if="orders.length" class="m-0">
                {{ 'Showing' | t }} {{ pagination?.totalResults }} {{ pagination?.totalResults > 1 ? 'results' : 'result' | t }}
              </p>
              <SloSelect v-model="queryParams.dateRange" class="marketplace--order-history-date-range">
                <option value="all" selected>{{ 'All Transactions' | t }}</option>
                <option value="3">{{ 'Last 3 months' | t }}</option>
                <option value="6">{{ 'Last 6 months' | t }}</option>
                <option value="12">{{ 'Last 12 months' | t }}</option>
              </SloSelect>
            </div>
            <SloLoader :loading="searchAndFilterLoading" size="xl">
              <div
                v-if="!hasSearchAndFilterResults && isSearchingOrFiltering"
                class="marketplace--order-history-no-results"
              >
                <p>{{ 'No orders found' | t }}</p>
              </div>
              <div v-for="(order, index) in orders" :key="`order-${index}`" class="mt-lg">
                <MarketplaceTable v-if="order?.orderEntries">
                  <template v-slot:header>
                    <MarketplaceTableHeader>
                      <template v-slot:titles>
                        <div class="flex items-start justify-between md:flex-col md:justify-start">
                          <h4 class="text-base fw-strong">{{ 'Order Number' | t }}</h4>
                          <span class="text-base c-gray-700">{{ order?.code | formatOrderNumber }}</span>
                        </div>
                        <div class="flex items-start justify-between md:flex-col md:justify-start">
                          <h4 class="text-base fw-strong">{{ 'Date Placed' | t }}</h4>
                          <span class="text-base c-gray-700">{{ order?.placed | formatDate }}</span>
                        </div>
                        <div class="flex items-start justify-between md:flex-col md:justify-start">
                          <h4 class="text-base fw-strong">{{ 'Items' | t }}</h4>
                          <span class="text-base c-gray-700">{{ order?.totalItems }}</span>
                        </div>
                        <div class="flex items-start justify-between md:flex-col md:justify-start">
                          <h4 class="text-base fw-strong">{{ 'Total' | t }}</h4>
                          <span class="text-base c-gray-700">{{ order?.total?.formattedValue }}</span>
                        </div>
                      </template>
                      <template v-slot:buttons>
                        <ReorderOrderButton :order="order" />
                        <SloButton
                          :to="{
                            name: 'marketplace-account-order',
                            params: {code: order?.code},
                            query: currentRouteFullPath ? {back: currentRouteFullPath} : {}
                          }"
                          fill="outline"
                        >
                          {{ 'View Order' | t }}
                        </SloButton>
                      </template>
                    </MarketplaceTableHeader>
                  </template >
                  <template v-slot:body>
                    <MarketplaceRow
                      v-for="(orderEntry, index) in order?.orderEntries.slice(0, ordersEntriesDisplayLength[order?.code] || 3)"
                      :key="`order-entry-${index}`"
                      :title="orderEntry?.product?.number"
                      :description="orderEntry?.product?.title"
                      :sku="orderEntry?.product?.code"
                      :product-type="orderEntry?.product?.type"
                      :price="getProductPrice(orderEntry)"
                      :uom="orderEntry?.product?.uom"
                      :quantity="orderEntry?.quantity"
                      :total="orderEntry?.totalPrice?.formattedValue"
                      :show-reorder-button="!orderEntry?.product?.subscriptionNumber"
                      :subscription-id="orderEntry?.product?.subscriptionNumber"
                      :subscription-frequency="orderEntry?.product?.formattedFrequency"
                      :subscription-percentage-discount="orderEntry?.product?.subscriptionPercentageDiscount"
                    >
                      <template v-slot:image>
                        <SloProductImage :source="orderEntry?.product?.images | getProductThumbnail" />
                      </template>
                      <template
                        v-if="orderEntry?.product?.subscriptionNumber && !orderEntry?.isCancelled"
                        v-slot:subscription-update
                      >
                        <router-link
                          :to="{
                            name: 'marketplace-account-subscription',
                            params: {subscriptionId: orderEntry?.product?.subscriptionNumber}
                          }"
                          class="text-sm fw-strong mt-sm block"
                        >
                          {{ 'Edit Subscription' | t }}
                        </router-link>
                      </template>
                    </MarketplaceRow>
                  </template >
                  <template v-slot:footer v-if="order?.orderEntries.length > 3 || orderIsCancelled(order)">
                    <MarketplaceTableFooter v-if="order?.orderEntries.length > 3">
                      <SloButton
                        v-if="ordersEntriesDisplayLength[order?.code]"
                        @click="removeOrdersEntriesDisplayLengthItem(order)"
                        fill="clear"
                        class="flex items-center gap-xs p-0 bg-transparent c-blue-400 hover:c-blue-700"
                      >
                        <SloIcon name="Minus" />
                        {{ 'Collapse' | t }}
                      </SloButton>
                      <div v-else class="flex items-baseline justify-start gap-sm">
                        <SloButton
                          @click="addOrdersEntriesDisplayLengthItem(order)"
                          fill="clear"
                          class="flex items-center gap-xs p-0 bg-transparent c-blue-400 hover:c-blue-700"
                        >
                          <SloIcon name="Plus" />
                          {{ order?.orderEntries.length - 3 }}
                          {{ 'more items' | t }}
                        </SloButton>
                        <div class="marketplace--order-history-more-items">
                          <SloProductImage
                            v-for="(orderEntry, moreOrderEntryKey) in order?.orderEntries.slice(3, order?.orderEntries.length)"
                            :key="`more-order-entry-${moreOrderEntryKey}`"
                            :source="orderEntry?.product?.images | getProductThumbnail"
                          />
                        </div>
                      </div>
                    </MarketplaceTableFooter>
                    <div v-if="orderIsCancelled(order)" class="customer-return">
                      <p class="flex items-start gap-sm m-0">
                        <span class="text-base fw-strong">{{ 'Reason' | t }}</span>
                        {{ order?.cancellationReason }}
                      </p>
                    </div>
                  </template >
                </MarketplaceTable>
              </div>
              <SloPagination
                v-if="pagination.totalResults > pagination.pageSize"
                :total-pages="pagination.totalPages"
                :current-page="parseInt(queryParams.currentPage)"
                :max-visible-pages="10"
                @pageChanged="handlePageChange"
              />
            </SloLoader>
          </SloLoader>
        </div>
      </main>
    </section>
  </Layout>
</template>

<script>
import {debounce} from 'vue-debounce';
import t from '../../main/i18n';
import formatDate from '../../../lib/filters/formatDate';
import {marketplaceApi} from '../apis/marketplace';
import {getProductThumbnail, formatOrderNumber, handleApiErrors} from '../../main/utils';
import Layout from '../components/Layout.vue';
import SloLoader from '../../../lib/components/SloLoader.vue';
import SloField from '../../../lib/components/SloField.vue';
import SloInput from '../../../lib/components/SloInput.vue';
import SloLabel from '../../../lib/components/SloLabel.vue';
import SloButton from '../../../lib/components/SloButton.vue';
import SloIcon from '../../../lib/components/SloIcon.vue';
import SloTabs from '../../../lib/components/SloTabs.vue';
import SloTab from '../../../lib/components/SloTab.vue';
import SloSelect from '../../../lib/components/SloSelect.vue';
import SloProductImage from '../../../lib/components/SloProductImage.vue';
import SloPagination from '../../../lib/components/SloPagination.vue';
import MarketplaceTable from '../components/MarketplaceTable.vue';
import MarketplaceRow from '../components/MarketplaceRow.vue';
import MarketplaceTableHeader from '../components/MarketplaceTableHeader.vue';
import EmptyCartTeaser from '../components/EmptyCartTeaser.vue';
import MarketplaceTableFooter from '../components/MarketplaceTableFooter.vue';
import ReorderOrderButton from '../components/ReorderOrderButton.vue';

export default {
  components: {
    Layout,
    EmptyCartTeaser,
    SloLoader,
    SloField,
    SloInput,
    SloLabel,
    SloButton,
    SloIcon,
    SloSelect,
    SloTabs,
    SloTab,
    SloProductImage,
    SloPagination,
    MarketplaceTable,
    MarketplaceRow,
    MarketplaceTableHeader,
    MarketplaceTableFooter,
    ReorderOrderButton
  },
  filters: {
    t,
    formatDate,
    getProductThumbnail,
    formatOrderNumber
  },
  data() {
    return {
      loading: true,
      searchAndFilterLoading: false,
      isFirstPageLoad: true,
      orders: [],
      ordersEntriesDisplayLength: [],
      currentRouteFullPath: '',
      queryParams: {
        status: '',
        dateRange: 'all',
        currentPage: 1,
        searchTerm: ''
      },
      pagination: {
        pageSize: 3,
        totalPages: 0,
        totalResults: 0
      },
      startDate: '',
      endDate: ''
    };
  },
  created() {
    this.queryParams = {...this.queryParams, ...this.$route.query};
    this.currentRouteFullPath = this.$router.currentRoute.fullPath;
  },
  computed: {
    isSearchingOrFiltering() {
      return this.queryParams.searchTerm || this.queryParams.status || this.queryParams.dateRange !== 'all';
    },
    hasSearchAndFilterResults() {
      return this.isSearchingOrFiltering && this.pagination.totalResults > 0;
    }
  },
  watch: {
    $route() {
      this.currentRouteFullPath = this.$router.currentRoute.fullPath;
    },
    queryParams: {
      handler() {
        if (
          this.queryParams.searchTerm === ''
          || this.queryParams.searchTerm.length > 2
          || this.orders.length === 0
        ) {
          this.searchAndFilterLoading = true;
          this.setDateRangeValue();
          (debounce(() => this.updateQuery(), '300ms'))();
        }
      },
      deep: true
    },
    'queryParams.status'(newValue, oldValue) {
      if (newValue !== oldValue && !this.$route?.query?.currentPage) {
        this.queryParams.currentPage = 1;
      }
    },
    'queryParams.dateRange'(newValue, oldValue) {
      if (newValue !== oldValue && !this.$route?.query?.currentPage) {
        this.queryParams.currentPage = 1;
      }
    },
    'queryParams.searchTerm'(newValue, oldValue) {
      if (newValue !== oldValue && !this.$route?.query?.currentPage) {
        this.queryParams.currentPage = 1;
      }
    }
  },
  methods: {
    async updateQuery() {
      const query = Object.entries(this.queryParams).reduce((acc, [key, val]) => {
        if (!val) return acc;
        return {...acc, [key]: val};
      }, {});

      await this.getOrders();

      if (!this.isFirstPageLoad) {
        await this.$router.push({
          name: 'marketplace-account-orders',
          query: {...query}
        }).catch(() => {});
      }
      this.isFirstPageLoad = false;
    },
    setDateRangeValue() {
      this.loading = true;

      if (this.queryParams.dateRange === 'all') {
        this.startDate = '';
        this.endDate = '';
      } else {
        const dateInPast = new Date();
        dateInPast.setMonth(dateInPast.getMonth() - parseInt(this.queryParams.dateRange));
        this.startDate = formatDate(dateInPast, 'YYYY-MM-DD');
        this.endDate = formatDate(new Date(), 'YYYY-MM-DD');
      }
    },
    handleStatusChange(status) {
      this.queryParams.status = status;
    },
    handlePageChange(page) {
      window.scrollTo(0, 0);
      this.queryParams.currentPage = parseInt(page);
    },
    async getOrders() {
      await marketplaceApi.getCurrentUserOrders({
        currentPage: this.queryParams.currentPage - 1,
        pageSize: this.pagination.pageSize,
        status: this.queryParams.status !== 'all' ? this.queryParams.status : false,
        startDate: this.startDate,
        endDate: this.endDate,
        searchTerm: this.queryParams.searchTerm
      })
        .then(res => {
          this.loading = false;
          this.searchAndFilterLoading = false;
          this.orders = res.data.orders;
          this.pagination = {
            currentPage: res.data?.pagination?.currentPage + 1,
            pageSize: res.data?.pagination?.pageSize,
            totalPages: res.data?.pagination?.totalPages,
            totalResults: res.data?.pagination?.totalResults
          };
        })
        .catch(e => {
          this.loading = false;
          this.searchAndFilterLoading = false;
          handleApiErrors(e, this, 'Error loading orders');
        });
    },
    orderIsCancelled(order) {
      return order?.status === 'S004' && order?.cancellationReason;
    },
    getProductPrice(orderEntry) {
      return orderEntry?.product?.subscriptionNumber
        ? orderEntry?.product?.subscriptionPrice?.formattedValue
        : orderEntry?.basePrice?.formattedValue;
    },
    addOrdersEntriesDisplayLengthItem(order) {
      this.ordersEntriesDisplayLength[order?.code] = order?.orderEntries.length;
      this.$forceUpdate();
    },
    removeOrdersEntriesDisplayLengthItem(order) {
      delete this.ordersEntriesDisplayLength[order?.code];
      this.$forceUpdate();
    }
  }
};
</script>

<style scoped lang="scss">
@import '../../../scss/variables';
@import '../../../scss/system';

.marketplace--order-history {
  :deep(.slo-tabs) {
    nav {
      overflow: scroll;

      &::-webkit-scrollbar {
        display: none;
      }
    }

    nav > ul {
      padding: 0;
      border-bottom: 1px solid $color-gray-300;
    }

    > main {
      margin-block: 3.2rem;
      overflow: initial;
    }
  }

  :deep(.slo-input-wrapper) {
    label {
      margin: 0;
    }
  }

  :deep(.slo-tab) {
    text-transform: uppercase;
    font-weight: 500;
  }

  &-date-range {
    min-width: 164px;
  }

  &-no-results {
    > p {
      margin: 3.2rem 0;
      font-size: 2.4rem;
    }
  }

  &-more-items {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 0.8rem;

    :deep(.slo-product-image) {
      img {
        width: 3.6rem;
        height: 3.6rem;
      }
    }
  }
}

.marketplace-collapse-toggle {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0;
  background-color: #fff;
  color: $color-blue-400;

  &:hover, &:focus {
    color: $color-blue-700;
  }
}

.customer-return {
  padding: 1.6rem;
  border-top: 1px solid $color-gray-400;
  background-color: #fffbeb;
  border-radius: 0 0 1.2rem 1.2rem;
}

.scroll-x-mobile {
  overflow-x: auto;
  scroll-behavior: smooth;
  -webkit-overflow-scrolling: touch;
  -ms-overflow-style: -ms-autohiding-scrollbar;

  @media (min-width: $screen-sm) {
    overflow-x: initial;
  }
}
</style>
